/**
 * Waiting for the new worker to actually take control.
 *
 * `skipWaiting()` is asynchronous. Posting SKIP_WAITING and reloading on the
 * next line is a race, and on dev it lost: the window reloaded, came back
 * controlled by the OLD worker, and the new one sat in `installed` forever.
 * The reload-loop guard then correctly reported "reloading has not helped" —
 * the update path could not complete at all.
 *
 * Waiting for `controllerchange` HERE is not the banned pattern. What is
 * banned is *reloading every open window* when the controller changes. This
 * only resolves a promise, in the one window whose owner pressed the button,
 * and that window alone then reloads.
 */

export const CONTROL_TIMEOUT_MS = 5_000;

export interface ControllerPorts {
  /** Subscribe to controllerchange; returns an unsubscribe. */
  onControllerChange: (fn: () => void) => () => void;
  /** Ask the waiting worker to take over. */
  postSkipWaiting: () => void;
  /** Returns a cancel. */
  setTimer: (fn: () => void, ms: number) => () => void;
}

export interface ControlResult {
  /** false = the worker never took control in time; we reload anyway, and
   *  the loop guard will notice if that keeps not helping. */
  controlled: boolean;
}

export const awaitController = (
  p: ControllerPorts,
  timeoutMs = CONTROL_TIMEOUT_MS,
): Promise<ControlResult> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = (controlled: boolean) => {
      if (settled) return;
      settled = true;
      off();
      cancel();
      resolve({ controlled });
    };

    // ⚠️ Subscribe BEFORE asking. The worker can take control faster than
    // this function returns, and an event that fires before we are listening
    // is simply lost — which would reintroduce the very race this fixes.
    const off = p.onControllerChange(() => finish(true));
    const cancel = p.setTimer(() => finish(false), timeoutMs);
    p.postSkipWaiting();
  });
